import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import PropTypes from "prop-types";
import {createPortal} from "react-dom";
import {InformationCircleIcon, XIcon} from "@heroicons/react/outline";
import NumberFormat from "react-number-format";
import {getFormattedDate} from "../../../utils/timeUtils";
import {getPriceHistorySections, hasPriceHistoryEntries} from "./document-body-utils";
import {DOCUMENT_PORTAL_DROPDOWN_Z_INDEX_CLASS} from "../constants";

const PriceHistorySection = ({entries, label}) => {
    if (!Array.isArray(entries) || entries.length === 0) {
        return null;
    }

    return (
        <section className="rounded-lg border border-gray-200 bg-white">
            <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
                <p className="text-sm font-semibold text-gray-700">{label}</p>
                <span className="text-xs font-medium text-gray-400">{entries.length}</span>
            </div>
            <ul className="divide-y divide-gray-100">
                {entries.map((price, index) => (
                    <li key={price?.id ?? index} className="px-3 py-2">
                        <div className="flex items-center justify-between gap-4">
                            <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-medium text-gray-800">
                                    {getFormattedDate(price?.date)}
                                </p>
                                {price?.reference ? (
                                    <p className="text-xs text-gray-500">{price.reference}</p>
                                ) : null}
                            </div>
                            <NumberFormat
                                value={price?.price}
                                displayType="text"
                                thousandSeparator="."
                                decimalSeparator=","
                                decimalScale={4}
                                suffix={` ${price?.currency_symbol ?? "€"}`}
                                className="text-sm font-semibold text-blue-600"
                            />
                        </div>
                    </li>
                ))}
            </ul>
        </section>
    );
};

PriceHistorySection.propTypes = {
    entries: PropTypes.arrayOf(PropTypes.object),
    label: PropTypes.string.isRequired,
};

PriceHistorySection.defaultProps = {
    entries: [],
};

const PriceHistoryDropdown = ({prices, t}) => {
    const sections = getPriceHistorySections(prices, t);

    return (
        <div className="space-y-3">
            <p className="text-sm text-gray-500">
                {t("app.quotations.price_history_hint", {defaultValue: "Consulta rapidamente le variazioni registrate."})}
            </p>
            <nav className="space-y-3" aria-label={t("app.quotations.price_history", {defaultValue: "Storico prezzi"})}>
                {sections.map((section) => (
                    <PriceHistorySection
                        key={section.key}
                        entries={section.entries}
                        label={section.label}
                    />
                ))}
            </nav>
        </div>
    );
};

PriceHistoryDropdown.propTypes = {
    prices: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.object),
        PropTypes.object,
    ]),
    t: PropTypes.func.isRequired,
};

PriceHistoryDropdown.defaultProps = {
    prices: null,
};

const DocumentBodyNetPriceCell = ({value, symbol, prices, t, canViewHistory}) => {
    const hasHistory = canViewHistory && hasPriceHistoryEntries(prices);
    const [isOpen, setIsOpen] = useState(false);

    const closeDropdown = useCallback(() => {
        setIsOpen(false);
    }, []);

    const toggleDropdown = useCallback(() => {
        if (!hasHistory) {
            return;
        }
        setIsOpen((previous) => !previous);
    }, [hasHistory]);

    useEffect(() => {
        if (!isOpen || typeof document === "undefined") {
            return undefined;
        }
        const handleEscape = (event) => {
            if (event.key === "Escape") {
                event.preventDefault();
                closeDropdown();
            }
        };

        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("keydown", handleEscape);
        };
    }, [closeDropdown, isOpen]);

    useEffect(() => {
        if (!isOpen || typeof document === "undefined") {
            return undefined;
        }
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = originalOverflow;
        };
    }, [isOpen]);

    const formattedValue = useMemo(() => {
        const numericValue = Number(value);
        if (Number.isFinite(numericValue)) {
            return numericValue.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
        }
        return value ?? "";
    }, [value]);

    const historyLabel = t("app.quotations.price_history", {defaultValue: "Storico prezzi"});

    return (
        <div className="flex w-full items-center gap-2">
            <div className="flex-none">
                {hasHistory ? (
                    <button
                        type="button"
                        className="inline-flex  items-center justify-center"
                        onClick={toggleDropdown}
                        aria-label={historyLabel}
                        aria-expanded={isOpen}
                        aria-haspopup="dialog"
                    >
                        <InformationCircleIcon className="h-4 w-4 text-blue-500" aria-hidden="true" />
                    </button>
                ) : (
                    <div className="h-8 w-8 rounded-md border border-transparent" aria-hidden="true" />
                )}
            </div>
            <div className="flex-1">
                <div className="text-right">
                    <span className="block truncate text-sm text-gray-900">
                        {formattedValue}
                        {symbol ? ` ${symbol}` : ""}
                    </span>
                </div>
            </div>
            {isOpen && typeof document !== "undefined" && createPortal(
                <div className={`${DOCUMENT_PORTAL_DROPDOWN_Z_INDEX_CLASS} fixed inset-0 z-50 flex items-center justify-center p-4`}>
                    <div className="absolute inset-0 bg-gray-900/40" onClick={closeDropdown} aria-hidden="true" />
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-label={historyLabel}
                        className="relative z-10 w-full max-w-lg rounded-xl bg-white shadow-2xl ring-1 ring-black/5"
                    >
                        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
                            <p className="text-base font-semibold text-gray-900">{historyLabel}</p>
                            <button
                                type="button"
                                onClick={closeDropdown}
                                className="rounded p-1 text-gray-500 transition hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                                aria-label={t("app.common.close", {defaultValue: "Chiudi"})}
                            >
                                <XIcon className="h-5 w-5" aria-hidden="true" />
                            </button>
                        </div>
                        <div className="max-h-[70vh] overflow-y-auto px-4 py-4">
                            <PriceHistoryDropdown prices={prices} t={t} />
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

DocumentBodyNetPriceCell.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    symbol: PropTypes.string,
    prices: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.object),
        PropTypes.object,
    ]),
    t: PropTypes.func.isRequired,
    canViewHistory: PropTypes.bool,
};

DocumentBodyNetPriceCell.defaultProps = {
    value: "",
    symbol: "",
    prices: null,
    canViewHistory: true,
};

export default DocumentBodyNetPriceCell;
